import React, { useState, useEffect } from 'react';
import api from '../services/api';

const rs = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

// Lightweight inline-SVG pie (no external chart lib). slices: [{label,value,color}]
function Pie({ slices, size = 170 }) {
  const total = slices.reduce((s, x) => s + Math.max(0, x.value), 0);
  const r = size / 2, cx = r, cy = r;
  if (total <= 0) return <div style={{ color: '#666', fontSize: '0.8rem', padding: '1rem' }}>No data</div>;
  let acc = 0;
  const arc = (frac) => {
    const a = 2 * Math.PI * frac - Math.PI / 2;
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
  };
  return (
    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {slices.filter(s => s.value > 0).map((s, i) => {
          const frac = s.value / total;
          const [x1, y1] = arc(acc);
          acc += frac;
          const [x2, y2] = arc(acc);
          const large = frac > 0.5 ? 1 : 0;
          if (frac >= 0.9999) return <circle key={i} cx={cx} cy={cy} r={r} fill={s.color} />;
          return <path key={i} d={`M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`} fill={s.color} />;
        })}
        <circle cx={cx} cy={cy} r={r * 0.55} fill="#0d0d16" />
      </svg>
      <div style={{ fontSize: '0.78rem' }}>
        {slices.map((s, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', margin: '0.15rem 0' }}>
            <span style={{ width: 10, height: 10, background: s.color, borderRadius: 2, display: 'inline-block' }} />
            <span style={{ color: '#ccc' }}>{s.label}</span>
            <span style={{ color: '#888', marginLeft: 'auto' }}>{s.display != null ? s.display : rs(s.value)} ({total ? Math.round(s.value / total * 100) : 0}%)</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const SEG_COLORS = { options: '#00d9ff', equity: '#8b5cf6', futures: '#22c55e', sensex: '#f59e0b', commodity: '#ffb020', mcx_options: '#f97316' };

function Report() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let on = true;
    const load = () => api.weeklyReport().then(r => { if (on) { if (r.status === 'success') setData(r); else setErr(r.message || 'No data'); } }).catch(() => on && setErr('Could not load report'));
    load();
    const iv = setInterval(load, 15000);
    return () => { on = false; clearInterval(iv); };
  }, []);

  if (err) return <div style={{ padding: '1.5rem', color: '#ff5555' }}>{err}</div>;
  if (!data) return <div style={{ padding: '1.5rem', color: '#888' }}>Loading weekly report…</div>;

  const t = data.totals;
  const winLossPie = [
    { label: 'Wins', value: Math.abs(t.total_win), color: '#00ff88' },
    { label: 'Losses', value: Math.abs(t.total_loss), color: '#ff5555' },
  ];
  const segPie = Object.entries(data.by_segment || {}).map(([k, v]) => ({
    label: k, value: Math.abs(v), display: rs(v), color: SEG_COLORS[k] || '#888',
  }));
  const maxNet = Math.max(1, ...data.days.map(d => Math.abs(d.net)));

  return (
    <div style={{ padding: '1rem' }}>
      <div className="card full-width">
        <div className="card-header">
          <span className="card-title">📊 Weekly Report — this week (from Monday)</span>
          <span style={{ color: '#666', fontSize: '0.72rem' }}>{data.week_start ? `since ${data.week_start} · ` : ''}your own trades · auto-refreshes</span>
        </div>
        {/* summary tiles */}
        <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
          {[
            ['Net P&L', rs(t.net), t.net >= 0 ? '#00ff88' : '#ff5555'],
            ['Trades', t.trades, '#ccc'],
            ['Win rate', `${t.win_rate}%`, '#00d9ff'],
            ['Wins / Losses', `${t.wins} / ${t.losses}`, '#ccc'],
            ['Avg win', rs(t.avg_win), '#00ff88'],
            ['Avg loss', rs(t.avg_loss), '#ff5555'],
          ].map(([label, val, color], i) => (
            <div key={i} style={{ background: '#111', border: '1px solid #2a2a3e', borderRadius: 8, padding: '0.6rem 0.9rem', minWidth: 110 }}>
              <div style={{ color: '#888', fontSize: '0.68rem' }}>{label}</div>
              <div style={{ color, fontSize: '1.05rem', fontWeight: 700 }}>{val}</div>
            </div>
          ))}
        </div>

        {/* pie charts */}
        <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
          <div>
            <div style={{ color: '#aaa', fontSize: '0.8rem', marginBottom: '0.4rem' }}>Wins vs Losses (₹)</div>
            <Pie slices={winLossPie} />
            {t.avg_loss < 0 && Math.abs(t.avg_loss) > t.avg_win && (
              <div style={{ color: '#f59e0b', fontSize: '0.7rem', marginTop: '0.4rem', maxWidth: 260 }}>
                ⚠ Avg loss ({rs(t.avg_loss)}) is bigger than avg win ({rs(t.avg_win)}) — the risk:reward gap.
              </div>
            )}
          </div>
          {segPie.length > 0 && (
            <div>
              <div style={{ color: '#aaa', fontSize: '0.8rem', marginBottom: '0.4rem' }}>P&L by segment</div>
              <Pie slices={segPie} />
            </div>
          )}
        </div>
      </div>

      {/* per-day bars */}
      <div className="card full-width">
        <div className="card-header"><span className="card-title">Daily P&L</span></div>
        {data.days.length === 0 ? <p style={{ color: '#888' }}>No trades in the last 7 days.</p> : data.days.map((d, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', margin: '0.3rem 0', fontSize: '0.8rem' }}>
            <span style={{ width: 90, color: '#aaa' }}>{d.date}</span>
            <div style={{ flex: 1, background: '#161622', borderRadius: 4, height: 18, position: 'relative' }}>
              <div style={{ position: 'absolute', left: d.net >= 0 ? '50%' : `${50 - (Math.abs(d.net) / maxNet) * 50}%`, width: `${(Math.abs(d.net) / maxNet) * 50}%`, height: '100%', background: d.net >= 0 ? '#00ff8855' : '#ff555555', borderRadius: 4 }} />
              <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 1, background: '#444' }} />
            </div>
            <span style={{ width: 90, textAlign: 'right', color: d.net >= 0 ? '#00ff88' : '#ff5555', fontWeight: 600 }}>{rs(d.net)}</span>
            <span style={{ width: 70, textAlign: 'right', color: '#777', fontSize: '0.72rem' }}>{d.wins}W/{d.losses}L</span>
          </div>
        ))}
      </div>

      {/* trade list */}
      <div className="card full-width">
        <div className="card-header"><span className="card-title">Trades (last 7 days)</span></div>
        <div style={{ maxHeight: 380, overflowY: 'auto' }}>
          <table style={{ width: '100%', fontSize: '0.75rem', borderCollapse: 'collapse' }}>
            <thead><tr style={{ color: '#888', textAlign: 'left' }}>
              <th style={{ padding: '0.3rem' }}>When</th><th>Symbol</th><th>Segment</th><th style={{ textAlign: 'right' }}>P&L</th><th>Reason</th>
            </tr></thead>
            <tbody>
              {data.trades.map((tr, i) => (
                <tr key={i} style={{ borderTop: '1px solid #1a1a2e' }}>
                  <td style={{ padding: '0.3rem', color: '#999' }}>{tr.date}</td>
                  <td style={{ color: '#ddd' }}>{tr.symbol}</td>
                  <td style={{ color: '#777' }}>{tr.segment}</td>
                  <td style={{ textAlign: 'right', color: tr.pnl >= 0 ? '#00ff88' : '#ff5555', fontWeight: 600 }}>{rs(tr.pnl)}</td>
                  <td style={{ color: '#888' }}>{tr.reason}</td>
                </tr>
              ))}
              {data.trades.length === 0 && <tr><td colSpan="5" style={{ color: '#666', padding: '0.6rem' }}>No trades yet this week.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default Report;
